import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert,
    Avatar,
    MenuItem,
    useTheme,
    IconButton,
    Snackbar,
    Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { auth } from '../config/firebase';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS } from '../config/api';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RankedMatchStats from './RankedMatchStats';

const Profile: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        nama: '',
        levelBermain: 'Beginner'
    });
    const [memberInfo, setMemberInfo] = useState<Array<{
        status: string;
        memberCode: string;
        activatedAt: string;
        remainingVisits: string;
        expiryDate: string;
    }>>([]);
    const [memberStatusLoading, setMemberStatusLoading] = useState(false);
    const [memberStatusError, setMemberStatusError] = useState<string | null>(null);
    const [copyFeedback, setCopyFeedback] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
    const [rankedStats, setRankedStats] = useState<any>(null);
    const [rankedStatsLoading, setRankedStatsLoading] = useState(false);
    const [rankedStatsError, setRankedStatsError] = useState<string | null>(null);

    useEffect(() => {
        // Ambil data booking terakhir
        const last = localStorage.getItem('lastBooking');
        if (last) {
            const lastData = JSON.parse(last);
            setFormData({
                nama: lastData.nama || '',
                levelBermain: lastData.level || 'Beginner'
            });
        } else {
            // Jika tidak ada, pakai data lama
            const savedNama = localStorage.getItem('nama') || '';
            const savedLevel = localStorage.getItem('levelBermain') || 'Beginner';
            setFormData({
                nama: savedNama,
                levelBermain: savedLevel
            });
        }
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const email = user?.email;
        if (!email) {
            setMemberInfo([]);
            return;
        }

        const fetchMemberStatus = async () => {
            setMemberStatusLoading(true);
            setMemberStatusError(null);

            try {
                const response = await axios.get(API_ENDPOINTS.MEMBERSHIP_AND_BOOKING, {
                    params: { email }
                });

                if (response.data?.success && Array.isArray(response.data.memberships)) {
                    // Filter hanya member dengan sisa > 0 dan hitung expiry date
                    const activeMembers = response.data.memberships
                        .filter((member: any) => {
                            const remaining = parseInt(member.remainingVisits, 10);
                            return !isNaN(remaining) && remaining > 0;
                        })
                        .map((member: any) => {
                            // Hitung batas tanggal mabar (27 hari dari tanggal pembelian)
                            const purchaseDate = new Date(member.activatedAt);
                            const expiryDate = new Date(purchaseDate.getTime() + (27 * 24 * 60 * 60 * 1000));
                            
                            // Format expiry date ke format Indonesia
                            const expiryDateFormatted = expiryDate.toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            });
                            
                            return {
                                ...member,
                                expiryDate: expiryDateFormatted
                            };
                        });
                    setMemberInfo(activeMembers);
                } else if (response.data?.success) {
                    setMemberInfo([]);
                } else {
                    setMemberInfo([]);
                    setMemberStatusError(response.data?.message || 'Tidak dapat memuat status member');
                }
            } catch (error: any) {
                console.error('Error fetching membership status:', error);
                setMemberInfo([]);
                setMemberStatusError('Gagal memuat status member');
            } finally {
                setMemberStatusLoading(false);
            }
        };

        fetchMemberStatus();
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const email = user?.email;
        if (!email) {
            setRankedStats(null);
            return;
        }

        const fetchRankedStats = async () => {
            setRankedStatsLoading(true);
            setRankedStatsError(null);

            try {
                // Fetch ranked leaderboard data
                const response = await axios.get('/api/bookings?action=ranked-leaderboard-local');
                const rankedData = response.data?.ranked_double_solo || [];

                console.log('Logged in email:', email);
                console.log('Ranked data count:', rankedData.length);
                console.log('Ranked data sample:', rankedData.slice(0, 3).map((p: any) => ({
                    nama: p.nama,
                    email: p.email
                })));

                // Find player by email
                const playerStats = rankedData.find((player: any) => {
                    const match = player.email && player.email.toLowerCase().trim() === email.toLowerCase().trim();
                    if (match) {
                        console.log('Found match:', player.nama, player.email);
                    }
                    return match;
                });

                if (playerStats) {
                    console.log('Player stats found:', playerStats);
                    setRankedStats(playerStats);
                } else {
                    console.log('No player stats found for email:', email);
                    setRankedStats(null);
                    setRankedStatsError('Data ranked match tidak ditemukan');
                }
            } catch (error: any) {
                console.error('Error fetching ranked stats:', error);
                setRankedStats(null);
                setRankedStatsError('Gagal memuat data ranked match');
            } finally {
                setRankedStatsLoading(false);
            }
        };

        fetchRankedStats();
    }, []);

    const handleCopyMemberCode = async (memberCode: string) => {
        try {
            await navigator.clipboard.writeText(memberCode);
            setCopyFeedback({ open: true, message: 'Kode member berhasil disalin' });
        } catch (error) {
            setCopyFeedback({ open: true, message: 'Gagal menyalin kode member' });
        }
    };

    const handleCloseCopyFeedback = () => {
        setCopyFeedback({ open: false, message: '' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Simpan ke localStorage
            localStorage.setItem('nama', formData.nama);
            localStorage.setItem('levelBermain', formData.levelBermain);
            setSuccess(true);
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            localStorage.clear();
            navigate('/login');
        } catch (error: any) {
            setError(error.message);
        }
    };

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    return (
        <>
            <Helmet>
                <title>Profile - RaketerS</title>
            </Helmet>
            <Container maxWidth="md" sx={{ mt: 4, px: { xs: 1, sm: 2 }, mb: 4 }}>
                {/* Card 1: User Profile Section */}
                <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Avatar
                            src={user.photoURL}
                            alt={user.displayName}
                            sx={{ width: 100, height: 100, mb: 2 }}
                        />
                        <Typography variant="h6" gutterBottom>
                            {user.displayName}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {user.email}
                        </Typography>
                    </Box>
                </Paper>

                {/* Card 2: Nama and Level Bermain Form */}
                <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Profil Bermain
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Profil berhasil disimpan
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Nama"
                            value={formData.nama}
                            onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                            margin="normal"
                        />

                        <TextField
                            fullWidth
                            select
                            label="Level Bermain"
                            value={formData.levelBermain}
                            onChange={(e) => setFormData({ ...formData, levelBermain: e.target.value })}
                            margin="normal"
                        >
                            <MenuItem value="Beginner">Beginner</MenuItem>
                            <MenuItem value="Intermediate">Intermediate</MenuItem>
                            <MenuItem value="Advanced">Advanced</MenuItem>
                        </TextField>

                        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Simpan Perubahan'}
                            </Button>

                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleLogout}
                                fullWidth
                            >
                                Logout
                            </Button>
                        </Box>
                    </form>

                    <Snackbar
                        open={copyFeedback.open}
                        autoHideDuration={2000}
                        onClose={handleCloseCopyFeedback}
                        message={copyFeedback.message}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    />
                </Paper>

                {/* Card 3: Info Member Section */}
                <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Info Member
                    </Typography>

                    {memberStatusError && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {memberStatusError}
                        </Alert>
                    )}

                    {memberStatusLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={40} />
                        </Box>
                    )}

                    {!memberStatusLoading && memberInfo.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Belum memiliki member aktif
                        </Typography>
                    )}

                    {!memberStatusLoading && memberInfo.length > 0 && (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            {memberInfo.map((member, index) => {
                                const isElite = member.status.toLowerCase().includes('elite');
                                const isBasic = member.status.toLowerCase().includes('basic');
                                
                                let bgColor = 'background.paper';
                                let borderColor = 'divider';
                                
                                if (isElite) {
                                    bgColor = theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)';
                                    borderColor = theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.4)' : 'rgba(244, 67, 54, 0.3)';
                                } else if (isBasic) {
                                    bgColor = theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.08)';
                                    borderColor = theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.4)' : 'rgba(33, 150, 243, 0.3)';
                                }
                                
                                return (
                                    <Paper key={`${member.memberCode}-${index}`} elevation={0} sx={{ p: 2, bgcolor: bgColor, border: '1px solid', borderColor: borderColor, width: '100%', minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)' }}>
                                            <strong>Member #{index + 1}:</strong> {member.status}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0, flex: '1 1 0' }}>
                                                <strong>Kode Member:</strong> {member.memberCode || '-'}
                                            </Typography>
                                            {member.memberCode && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopyMemberCode(member.memberCode)}
                                                    sx={{ p: 0.5, flexShrink: 0 }}
                                                    title="Salin kode member"
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'grid', gap: 0.5, gridTemplateColumns: '1fr' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                <strong>Aktif Sejak:</strong> {member.activatedAt || '-'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                <strong>Sisa Kedatangan:</strong> {member.remainingVisits || '-'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                    <strong>Batas Tanggal Mabar:</strong> {member.expiryDate || '-'}
                                                </Typography>
                                                <Tooltip 
                                                    title="Tanggal mabar terakhir bukan aktivitas pembelian booking terakhir"
                                                    arrow
                                                >
                                                    <InfoIcon sx={{ fontSize: '1rem', color: 'info.main', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </Paper>

                {/* Card 4: Ranked Match Stats Section */}
                {rankedStatsLoading && (
                    <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Paper>
                )}

                {!rankedStatsLoading && rankedStats && (
                    <Box sx={{ mb: 3 }}>
                        <RankedMatchStats 
                            stats={rankedStats}
                            googlePhotoURL={user.photoURL}
                        />
                    </Box>
                )}

                {!rankedStatsLoading && !rankedStats && rankedStatsError && (
                    <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
                        <Alert severity="info">
                            {rankedStatsError}
                        </Alert>
                    </Paper>
                )}
            </Container>
        </>
    );
};

export default Profile; 